import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { loadAoiPoints, waveHeightColor } from '../utils/aoiLoader';

// AOI labels become visible when zoomed in to ~100 km scale bar (zoom 7 at lat ~10°N)
const LABEL_MIN_ZOOM = 7;

export default function AOILayer({ scenarioId, visible }) {
  const map = useMap();
  const groupRef = useRef(null);

  // Create dedicated panes once.
  // aoiLabelsPane is separate from labelsPane (used by contour tooltips)
  // so we can toggle it independently.
  useEffect(() => {
    if (!map.getPane('labelsPane')) {
      map.createPane('labelsPane');
      map.getPane('labelsPane').style.zIndex = 700;
      map.getPane('labelsPane').style.pointerEvents = 'none';
    }
    if (!map.getPane('aoiPane')) {
      map.createPane('aoiPane');
      map.getPane('aoiPane').style.zIndex = 650;
    }
    if (!map.getPane('aoiLabelsPane')) {
      map.createPane('aoiLabelsPane');
      map.getPane('aoiLabelsPane').style.zIndex = 710;
      map.getPane('aoiLabelsPane').style.pointerEvents = 'none';
    }
  }, [map]);

  // Load and draw points whenever the scenario changes.
  useEffect(() => {
    if (groupRef.current) { map.removeLayer(groupRef.current); groupRef.current = null; }
    if (!scenarioId) return;

    let cancelled = false;
    loadAoiPoints(scenarioId)
      .then(points => {
        if (cancelled) return;
        const group = L.layerGroup();
        points.forEach(p => {
          const color = waveHeightColor(p.h) || '#999';
          const marker = L.circleMarker([p.lat, p.lon], {
            pane: 'aoiPane',
            radius: 6,
            weight: 1.4,
            color: '#ffffff',
            fillColor: color,
            fillOpacity: 0.95,
          });
          marker.bindPopup(
            `<div style="font:600 14px system-ui"><b>${p.name}</b></div>` +
            `<div style="font:12px system-ui;color:#556">Max wave height ` +
            `<b style="color:${color}">${p.h.toFixed(2)} m</b></div>`
          );
          marker.bindTooltip(p.name, {
            permanent: true,
            direction: 'bottom',
            offset: [0, -2],
            className: 'aoi-label',
            pane: 'aoiLabelsPane',
          });
          group.addLayer(marker);
        });
        groupRef.current = group;
        if (visible) group.addTo(map);

        // Set initial pane visibility based on current zoom
        setAoiLabelPaneVisible(map, map.getZoom() >= LABEL_MIN_ZOOM);
      })
      .catch(err => console.error('[AOI] load error:', err));

    return () => { cancelled = true; };
  }, [scenarioId, map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show/hide the entire aoiLabelsPane on zoom change
  useEffect(() => {
    function onZoomEnd() {
      setAoiLabelPaneVisible(map, map.getZoom() >= LABEL_MIN_ZOOM);
    }
    map.on('zoomend', onZoomEnd);
    return () => map.off('zoomend', onZoomEnd);
  }, [map]);

  // Toggle layer visibility without refetching.
  useEffect(() => {
    if (!groupRef.current) return;
    if (visible) groupRef.current.addTo(map);
    else map.removeLayer(groupRef.current);
  }, [visible, map]);

  useEffect(() => () => {
    if (groupRef.current) { map.removeLayer(groupRef.current); groupRef.current = null; }
  }, [map]);

  return null;
}

function setAoiLabelPaneVisible(map, show) {
  const pane = map.getPane('aoiLabelsPane');
  if (pane) pane.style.display = show ? '' : 'none';
}
